// Ulcinj travel guide — long-form localized SEO landing page (7 languages) with Article + FAQ + Breadcrumb schema.
// Visual layer: watercolor illustrations (public/guide/*.jpg), scroll-reveal animations, fact chips, photo mosaic, sticky TOC.
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Sun, Waves, Plane, Landmark, BedDouble, CalendarDays, Thermometer, Clock, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useHotel } from '../context/HotelContext';
import { getGuide } from '../i18n/guide';
import { sectionPath, homePath } from '../siteRoutes';
import { SEASON_SLUGS, monthName, getSeasonT } from '../i18n/seasons';
import { GuideMap } from './GuideMap';
import TripPlanner from './TripPlanner';
import './UlcinjGuide.css';

const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://larosa-negra.com').replace(/\/$/, '');

// Illustrations per section (watercolor, generated for this guide) — Old Town / Ada Bojana / Šaško Lake form the mosaic.
const ART = {
  'where-to-stay': { src: '/guide/sunset.jpg', alt: 'Evening walk on Long Beach in front of a small beachfront hotel, Ulcinj' },
  'velika-plaza': { src: '/guide/velika-plaza.jpg', alt: 'Velika Plaža – Long Beach Ulcinj, 13 km of fine sand and shallow water' },
  'getting-there': { src: '/guide/getting-there.jpg', alt: 'Coastal road along the Montenegrin Adriatic towards Ulcinj' },
  'when-to-visit': { src: '/guide/sunset.jpg', alt: 'September sunset on Velika Plaža, Ulcinj' },
};
const MOSAIC = [
  { src: '/guide/old-town.jpg', alt: 'Ulcinj Old Town (Stari Grad) above Mala Plaža at sunset' },
  { src: '/guide/ada-bojana.jpg', alt: 'Ada Bojana river island – fish restaurants and kite surfing' },
  { src: '/guide/saska-lake.jpg', alt: 'Šaško Lake – birdwatching and the ruins of Svač' },
];
const ICONS = { 'where-to-stay': BedDouble, 'velika-plaza': Waves, 'things-to-do': Landmark, 'getting-there': Plane, 'when-to-visit': CalendarDays };
const FACTS = [
  { icon: Waves, value: '13 km', key: 'beach' },
  { icon: Thermometer, value: '24–27 °C', key: 'sea' },
  { icon: Sun, value: '2 500 h', key: 'sun' },
  { icon: Clock, value: '90 min', key: 'airport' },
  { icon: MapPin, value: '5 km', key: 'town' },
];
const FACT_LABELS = {
  en: { beach: 'of sandy beach', sea: 'sea in summer', sun: 'of sunshine a year', airport: 'from the airports', town: 'to the Old Town' },
  me: { beach: 'pješčane plaže', sea: 'more ljeti', sun: 'sunca godišnje', airport: 'od aerodroma', town: 'do Starog grada' },
  sq: { beach: 'plazh me rërë', sea: 'deti në verë', sun: 'diell në vit', airport: 'nga aeroportet', town: 'deri te Kalaja' },
  de: { beach: 'Sandstrand', sea: 'Meer im Sommer', sun: 'Sonne im Jahr', airport: 'von den Flughäfen', town: 'zur Altstadt' },
  ru: { beach: 'песчаного пляжа', sea: 'море летом', sun: 'солнца в год', airport: 'от аэропортов', town: 'до Старого города' },
  pl: { beach: 'piaszczystej plaży', sea: 'morze latem', sun: 'słońca rocznie', airport: 'z lotnisk', town: 'do Starego Miasta' },
  hu: { beach: 'homokos part', sea: 'tenger nyáron', sun: 'napsütés évente', airport: 'a repülőterektől', town: 'az Óvárosig' },
};

export const buildGuideSchema = (lang, g, path) => ({
  '@context': 'https://schema.org',
  '@graph': [
    { '@type': 'Article', '@id': `${SITE_URL}${path}#article`, headline: g.title, description: g.metaDescription, inLanguage: lang,
      image: MOSAIC.map((m) => `${SITE_URL}${m.src}`),
      author: { '@type': 'Organization', name: 'Hotel La Rosa Negra', url: `${SITE_URL}/` },
      publisher: { '@type': 'Organization', name: 'Hotel La Rosa Negra', logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` } },
      mainEntityOfPage: `${SITE_URL}${path}`, dateModified: new Date().toISOString().slice(0, 10),
      about: [{ '@type': 'City', name: 'Ulcinj', sameAs: 'https://www.wikidata.org/wiki/Q622434' }, { '@type': 'Beach', name: 'Velika Plaža' }],
      speakable: { '@type': 'SpeakableSpecification', cssSelector: ['#guide-intro', '.guide-faq-a'] } },
    { '@type': 'FAQPage', mainEntity: g.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
    { '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Hotel La Rosa Negra', item: `${SITE_URL}${lang === 'en' ? '' : `/${lang}`}/` },
      { '@type': 'ListItem', position: 2, name: g.nav, item: `${SITE_URL}${path}` } ] },
  ],
});

// Adds .is-visible once the element scrolls into view (CSS handles the motion; SSR/prerender stays static).
export const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => entries.forEach((e) => { if (e.isIntersecting) { el.classList.add('is-visible'); io.disconnect(); } }), { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
};

// Observe every `.guide-reveal` descendant of the ref'd container (for pages that don't use <Reveal>).
export const useRevealAll = (deps = []) => {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;
    const els = [...root.querySelectorAll('.guide-reveal:not(.is-visible)')];
    const io = new IntersectionObserver((entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); } }), { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return ref;
};

const Reveal = ({ as: Tag = 'div', className = '', children, ...rest }) => {
  const ref = useReveal();
  return <Tag ref={ref} className={`guide-reveal ${className}`} {...rest}>{children}</Tag>;
};

const Figure = ({ src, alt, className = '', caption }) => (
  <figure className={`guide-figure ${className}`}>
    <img src={src} alt={alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
    {caption && <figcaption>{caption}</figcaption>}
  </figure>
);

const UlcinjGuide = () => {
  const { lang } = useLanguage();
  const { gallery } = useHotel();
  const { pathname } = useLocation();
  const g = getGuide(lang);
  const path = pathname.replace(/\/$/, '');
  const [active, setActive] = useState(g.sections[0]?.id);
  const base = homePath(pathname) === '/' ? '' : homePath(pathname);

  useEffect(() => {
    const id = 'guide-jsonld';
    document.getElementById(id)?.remove();
    const s = document.createElement('script');
    s.type = 'application/ld+json'; s.id = id;
    s.textContent = JSON.stringify(buildGuideSchema(lang, g, path));
    document.head.appendChild(s);
    return () => document.getElementById(id)?.remove();
  }, [lang, g, path]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }), { rootMargin: '-40% 0px -50% 0px' });
    g.sections.forEach((s) => { const el = document.getElementById(s.id); if (el) io.observe(el); });
    return () => io.disconnect();
  }, [g]);

  const capOf = (p) => (p.caption_translations && (p.caption_translations[lang] || p.caption_translations.en)) || p.caption || p.alt || '';
  const enCap = (p) => (p.caption_translations && p.caption_translations.en) || p.caption || p.alt || '';
  const beachPhoto = [...(gallery || [])].map((p) => { const c = enCap(p).toLowerCase(); let sc = 0; for (const [k, w] of [['beach', 6], ['shoreline', 6], ['turquoise', 5], ['sea', 3], ['sunset', 3], ['aerial', 3], ['pool', 2], ['terrace', 1]]) if (c.includes(k)) sc += w; if (/bathroom|toilet|shower|sink/.test(c)) sc -= 20; return { p, sc }; }).sort((a, b) => b.sc - a.sc).map((x) => x.p)[0];
  const labels = FACT_LABELS[lang] || FACT_LABELS.en;

  const toc = g.sections.map((s) => {
    const Icon = ICONS[s.id] || Sun;
    return (
      <li key={s.id}>
        <a href={`#${s.id}`} className={`guide-toc-link ${active === s.id ? 'is-active' : ''}`} data-testid={`guide-toc-${s.id}`}><Icon className="w-4 h-4 shrink-0" /><span>{s.heading.split(':')[0]}</span></a>
      </li>
    );
  });

  const sections = g.sections.map((s, idx) => {
    const Icon = ICONS[s.id] || Sun;
    const art = ART[s.id];
    return (
      <Reveal as="section" key={s.id} id={s.id} className="guide-section scroll-mt-32" data-testid={`guide-section-${s.id}`}>
        <div className="guide-kicker"><span className="guide-kicker-num">{String(idx + 1).padStart(2, '0')}</span><Icon className="w-5 h-5 text-orange-500" /></div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-5 leading-tight">{s.heading}</h2>
        {art && <Figure src={art.src} alt={art.alt} className={`mb-6 ${idx % 2 ? 'guide-tilt-l' : 'guide-tilt-r'}`} />}
        {s.id === 'things-to-do' && (
          <div className="guide-mosaic mb-6" data-testid="guide-mosaic">
            {MOSAIC.map((m, i) => <Figure key={m.src} src={m.src} alt={m.alt} className={`guide-mosaic-${i}`} caption={m.alt.split(' – ')[0].split(' (')[0]} />)}
          </div>
        )}
        {s.paragraphs.map((p, i) => <p key={i} className={`text-base text-gray-700 leading-relaxed mb-4 ${i === 0 ? 'guide-dropcap' : ''}`}>{p}</p>)}
        {s.id === 'things-to-do' && <TripPlanner />}
        {s.id === 'getting-there' && <GuideMap lang={lang} />}
        {s.id === 'when-to-visit' && (
          <div className="flex flex-wrap gap-2 mb-5" data-testid="guide-month-links">
            {SEASON_SLUGS.map((m) => <Link key={m} to={`${base}/ulcinj-in-${m}`} className="guide-chip" data-testid={`guide-month-${m}`}><Sun className="w-3.5 h-3.5" /> {getSeasonT(lang).nav.split(' ')[0]} · {monthName(lang, m, true)}</Link>)}
          </div>
        )}
        {s.cta && (
          <Link to={sectionPath(s.cta.to, pathname)} className="guide-cta" data-testid={`guide-cta-${s.id}`}>
            {s.cta.label} <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </Reveal>
    );
  });

  const faq = g.faq.map((f, i) => (
    <details key={i} className="group guide-faq-item" data-testid={`guide-faq-${i}`}>
      <summary className="cursor-pointer font-semibold text-gray-900 list-none flex justify-between gap-4 py-4">{f.q}<span className="guide-plus">+</span></summary>
      <p className="guide-faq-a pb-4 text-sm text-gray-700 leading-relaxed">{f.a}</p>
    </details>
  ));

  return (
    <article className="guide-root py-14 sm:py-20" data-testid="ulcinj-guide">
      <div className="container mx-auto px-4">
        <Reveal className="guide-lead grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-center mb-14">
          <p id="guide-intro" className="text-lg sm:text-xl text-gray-800 leading-relaxed guide-dropcap" data-testid="guide-intro">{g.intro}</p>
          {beachPhoto && <Figure src={beachPhoto.url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL || ''}${beachPhoto.url}` : beachPhoto.url} alt={capOf(beachPhoto) || 'Velika Plaža beach in front of Hotel La Rosa Negra'} className="guide-hero-photo" />}
        </Reveal>
        <Reveal className="guide-facts mb-16" data-testid="guide-facts">
          {FACTS.map((f, i) => { const I = f.icon; return (
            <div key={f.key} className="guide-fact" style={{ '--i': i }} data-testid={`guide-fact-${f.key}`}>
              <I className="w-5 h-5 text-orange-500" /><strong>{f.value}</strong><span>{labels[f.key]}</span>
            </div>
          ); })}
        </Reveal>
        <div className="grid lg:grid-cols-[1fr_300px] gap-12">
          <div className="max-w-3xl space-y-16">
            <ol className="lg:hidden guide-toc-mobile">{toc}</ol>
            {sections}
            <Reveal as="section" id="guide-faq" className="guide-section" data-testid="guide-faq">
              <div className="guide-kicker"><span className="guide-kicker-num">FAQ</span></div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">FAQ · Ulcinj</h2>
              {faq}
            </Reveal>
          </div>
          <aside className="hidden lg:block">
            <div className="sticky top-32 space-y-6">
              <nav aria-label="Contents" className="guide-toc"><ol className="space-y-1">{toc}</ol></nav>
              <div className="guide-book-card" data-testid="guide-book-card">
                <div className="guide-book-glow" aria-hidden="true" />
                <h3 className="text-lg font-bold relative">{g.ctaTitle}</h3>
                <p className="text-sm text-gray-300 mt-2 relative">{g.ctaText}</p>
                <Link to={`${base}/booking`} className="relative mt-4 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full px-5 py-2.5 text-sm transition-colors" data-testid="guide-book-btn">
                  {g.ctaButton} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </article>
  );
};

export default UlcinjGuide;
